<template>
  <b-modal
    id="modal-merchant-add"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="ADD NEW MERCHANT"
  >
    <b-overlay
      :show="modalLoading || configLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="createMerchantForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickCreateMerchant)"
        >
          <!-- Dynamic Sections Loop -->
          <div
            v-for="sectionName in visibleSections"
            :key="sectionName"
            class="registration-section mb-2"
          >
            <!-- Section Header -->
            <b-row class="mb-2">
              <b-col
                cols="12"
                class="d-flex justify-content-center"
              >
                <div class="d-flex align-items-center mt-1 mb-1">
                  <feather-icon
                    :icon="getSectionIcon(sectionName)"
                    size="19"
                  />
                  <h4 class="mb-0 ml-50 sub-heading font-weight-bolder">
                    {{ getSectionDisplayTitle(sectionName) }}
                  </h4>
                </div>
              </b-col>
            </b-row>

            <!-- Section Fields Grid -->
            <b-row>
              <b-col
                v-for="field in getEnabledFieldsBySection(sectionName)"
                :key="field.key"
                :cols="12"
                :md="getFieldColSpan(field)"
              >
                <!-- Country Dropdown (Loads States on change) -->
                <b-form-group
                  v-if="field.key === 'country_id'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <v-select
                      :id="field.key"
                      v-model="form.country"
                      :options="countryOptions"
                      :reduce="option => option.id"
                      label="name"
                      autocomplete="nope"
                      :placeholder="field.placeholder || 'Select the country'"
                      @input="fetchStateList($event)"
                    >
                      <template #option="option">
                        {{ option.name }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- State Dropdown (Loads Cities on change) -->
                <b-form-group
                  v-else-if="field.key === 'state_id'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <v-select
                      :id="field.key"
                      v-model="form.state"
                      :options="stateOptions"
                      :reduce="option => option.id"
                      label="name"
                      autocomplete="nope"
                      :placeholder="field.placeholder || 'Select the state'"
                      @input="fetchCityList($event)"
                    >
                      <template #option="option">
                        {{ option.name }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- City Dropdown -->
                <b-form-group
                  v-else-if="field.key === 'city_id'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <v-select
                      :id="field.key"
                      v-model="form.city"
                      :options="cityOptions"
                      :reduce="option => option.id"
                      label="name"
                      autocomplete="nope"
                      :placeholder="field.placeholder || 'Select the city'"
                    >
                      <template #option="option">
                        {{ option.name }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Merchant Type Radio -->
                <b-form-group
                  v-else-if="field.key === 'merchant_type'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                  class="text-center"
                >
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-radio-group
                      :id="field.key"
                      v-model="merchantTypeSelected"
                      :options="getRadioOptions(field)"
                      class="mb-1"
                      value-field="value"
                      text-field="text"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Remarks Textarea with counter -->
                <b-form-group
                  v-else-if="field.key === 'description'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-textarea
                      :id="field.key"
                      v-model="form.description"
                      :placeholder="field.placeholder || 'Enter remarks'"
                      :class="form.description.length >= maxChar ? 'text-danger' : ''"
                      :maxlength="maxChar"
                      :state="errors.length > 0 ? false : (form.description.length < maxChar ? null : false)"
                      style="overflow: auto; scrollbar-width: none;"
                      :rows="3"
                    />
                    <small
                      class="textarea-counter-value float-right"
                      :class="form.description.length >= maxChar ? 'bg-danger' : ''"
                    >
                      <span class="char-count">{{ form.description.length }}</span> / {{ maxChar }}
                    </small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- File Inputs -->
                <b-form-group
                  v-else-if="field.type === 'file'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <div class="d-inline-flex align-items-center">
                      <span>{{ field.label }}</span>
                      <span
                        v-if="field.required"
                        class="text-danger mr-25"
                      > *</span>
                      <feather-icon
                        :id="'tooltip-staff-' + field.key"
                        icon="InfoIcon"
                        size="14"
                        class="text-muted ml-50 cursor-pointer"
                      />
                      <b-tooltip
                        :target="'tooltip-staff-' + field.key"
                        triggers="hover"
                      >
                        {{ verificationDocTip }}
                      </b-tooltip>
                    </div>
                  </template>

                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <div class="position-relative">
                      <b-form-file
                        :id="field.key"
                        :value="getFileFieldValue(field.key)"
                        accept=".pdf,image/*"
                        :placeholder="field.placeholder || 'Attach image/pdf'"
                        drop-placeholder="Drop file here..."
                        @change="handleFileChange(field.key, $event)"
                      />
                      <div
                        v-if="getFileFieldValue(field.key)"
                        class="upload-cancel"
                        @click="clearFileField(field.key)"
                      >
                        <feather-icon icon="XIcon" />
                      </div>
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Text / Number Inputs -->
                <b-form-group
                  v-else-if="field.type === 'text' || field.type === 'number'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-input
                      :id="field.key"
                      :value="getFieldValue(field.key)"
                      :type="getInputHtmlType(field)"
                      :placeholder="field.placeholder || ''"
                      :state="errors.length > 0 ? false : null"
                      autocomplete="nope"
                      @input="setFieldValue(field.key, $event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Dropdown Select -->
                <b-form-group
                  v-else-if="field.type === 'select'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <v-select
                      :id="field.key"
                      :value="getFieldValue(field.key)"
                      :options="field.options || []"
                      :placeholder="field.placeholder || 'Select option'"
                      @input="setFieldValue(field.key, $event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Radio Group -->
                <b-form-group
                  v-else-if="field.type === 'radio'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-radio-group
                      :id="field.key"
                      :value="getFieldValue(field.key)"
                      :options="getRadioOptions(field)"
                      class="my-1"
                      @input="setFieldValue(field.key, $event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Custom Checkbox -->
                <b-form-group
                  v-else-if="field.type === 'checkbox'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-checkbox
                      :id="field.key"
                      :checked="getFieldValue(field.key)"
                      @change="setFieldValue(field.key, $event)"
                    >
                      {{ field.label }}
                      <span
                        v-if="field.required"
                        class="text-danger"
                      > *</span>
                    </b-form-checkbox>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- Generic Textarea -->
                <b-form-group
                  v-else-if="field.type === 'textarea'"
                  :id="'group-' + field.key"
                  :label-for="field.key"
                >
                  <template #label>
                    <span>{{ field.label }}</span>
                    <span
                      v-if="field.required"
                      class="text-danger"
                    > *</span>
                  </template>
                  <ValidationProvider
                    #default="{ errors }"
                    :name="field.label"
                    :rules="getFieldValidationRules(field)"
                  >
                    <b-form-textarea
                      :id="field.key"
                      :value="getFieldValue(field.key)"
                      :placeholder="field.placeholder || ''"
                      rows="3"
                      :state="errors.length > 0 ? false : null"
                      @input="setFieldValue(field.key, $event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Staff-only Status Checkbox (Appears under Business Details) -->
            <b-row v-if="sectionName === 'Business Details'">
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  label="Status"
                  label-for="status"
                >
                  <b-form-checkbox
                    id="status"
                    v-model="form.status"
                    value="active"
                    unchecked-value="inactive"
                    class="mt-1"
                  >
                    Active
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Submit and Reset Buttons -->
          <b-row class="mt-2">
            <b-col
              cols="12"
              class="d-flex justify-content-center"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="mr-1 custom-button-color"
              >
                Submit
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="clearForm"
              >
                Clear
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BFormCheckbox,
  BFormTextarea,
  BRow,
  BOverlay,
  VBModal,
  BFormRadioGroup,
  BFormFile,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

const ResourceRepository = RepositoryFactory.get('resource')
const MerchantRepository = RepositoryFactory.get('merchant')
const MerchantSettingsRepository = RepositoryFactory.get('merchantSetting')
const MerchantRegistrationBuilderRepository = RepositoryFactory.get('merchantRegistrationBuilder')

function initialState() {
  return {
    open: false,
    id: null,
    form: {
      name: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      postalCode: '',
      country: null,
      state: null,
      city: null,
      contactNo: '',
      email: '',
      description: '',
      status: 'active',
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      userEmail: '',
      userContactNo: '',
      accountName: '',
      accountNo: '',
      bankName: '',
      branchName: '',
      branchCode: '',
      merchantVerifyDoc: null,
      userVerifyDoc: null,
      bankDetailsVerifyDoc: null,
      custom_fields: {},
    },
    modalLoading: false,
    configLoading: false,
    loading: false,
    countryOptions: [],
    stateOptions: [],
    cityOptions: [],
    configuredFields: [],
    configuredSections: [],
    merchantType: false,
    merchantVerifyDocEnable: false,
    merchantTypeSelected: 'credit',
    merchantTypeOptions: [
      { text: 'Credit', value: 'credit', disabled: false },
      { text: 'Cash', value: 'cash', disabled: false },
    ],
    search: '',
    verificationDocTip: 'Ensure your file is 2MB or less before uploading',
    maxChar: 750,
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BFormCheckbox,
    BFormTextarea,
    BRow,
    BOverlay,
    'v-select': vSelect,
    BFormRadioGroup,
    BFormFile,
    BTooltip,
    FeatherIcon,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  computed: {
    visibleSections() {
      const active = []
      this.configuredFields
        .filter(f => this.isFieldVisible(f))
        .forEach(f => {
          const { section } = f
          const sec = section || 'Additional Details'
          if (!active.includes(sec)) {
            active.push(sec)
          }
        })
      return active
    },
  },
  methods: {
    async openModal() {
      Object.assign(this.$data, initialState())
      this.open = true
      this.modalLoading = true
      await Promise.all([
        this.fetchConfiguration(),
        this.fetchCountryList(),
        this.getMerchantSetting(),
      ])
      this.modalLoading = false
    },

    async fetchConfiguration() {
      this.configLoading = true
      try {
        const response = await MerchantRegistrationBuilderRepository.getConfiguration()
        const { data: responseBody } = response
        const configData = responseBody.data ? responseBody.data : responseBody
        const { fields = [], sections = [] } = configData

        this.configuredSections = sections
        this.configuredFields = fields.map(f => {
          const { section, width, type } = f
          let colWidth = width
          if (!colWidth) {
            colWidth = (type === 'textarea' || f.key === 'business_name' || f.key === 'description') ? 'full' : 'half'
          }
          return {
            ...f,
            section: this.normalizeSectionName(section),
            width: colWidth,
            enabled: Boolean(f.enabled),
            required: Boolean(f.required),
            options: Array.isArray(f.options) ? f.options : [],
          }
        })
      } catch (err) {
        this.convertAndNotifyError(err)
      } finally {
        this.configLoading = false
      }
    },

    isVerificationDocField(key) {
      return [
        'merchant_verify_doc',
        'user_verify_doc',
        'bank_details_verify_doc',
      ].includes(key)
    },

    isFieldVisible(field) {
      if (!field.enabled) {
        return false
      }
      if (field.key === 'merchant_type' && !this.merchantType) {
        return false
      }
      if (this.isVerificationDocField(field.key) && !this.merchantVerifyDocEnable) {
        return false
      }
      return true
    },

    normalizeSectionName(section) {
      if (!section) return 'Business Details'
      if (section === 'Merchant Primary Business Details') return 'Business Details'
      if (section === 'Merchant Admin Details') return 'Admin Details'
      return section
    },

    getSectionDisplayTitle(section) {
      if (section === 'Business Details') return 'Merchant Primary Business Details'
      if (section === 'Admin Details') return 'Merchant Admin Details'
      if (section === 'Merchant Type') return 'Merchant Type'
      return section
    },

    getSectionIcon(section) {
      if (section === 'Business Details') return 'BriefcaseIcon'
      if (section === 'Admin Details') return 'UserIcon'
      if (section === 'Bank Details') return 'DollarSignIcon'
      if (section === 'Merchant Type') return 'CheckCircleIcon'
      return 'FileTextIcon'
    },

    getEnabledFieldsBySection(sectionName) {
      return this.configuredFields
        .filter(f => f.section === sectionName && this.isFieldVisible(f))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },

    getFieldColSpan(field) {
      const { width, type, key } = field
      if (width === 'full' || type === 'textarea' || key === 'business_name' || key === 'description' || key === 'merchant_type') {
        return 12
      }
      return 6
    },

    getInputHtmlType(field) {
      const { key, type } = field
      if (key === 'password') return 'password'
      if (key === 'email' || key === 'user_email') return 'email'
      if (type === 'number') return 'number'
      return 'text'
    },

    getFieldValidationRules(field) {
      const { required, key, type } = field
      const isEmail = key === 'email' || key === 'user_email' || type === 'email'

      if (this.isVerificationDocField(key)) {
        return (required && this.merchantVerifyDocEnable) ? 'required' : ''
      }

      if (required) {
        return isEmail ? 'required|email' : 'required'
      }
      return isEmail ? 'email' : ''
    },

    getRadioOptions(field) {
      const { options, key } = field
      if (Array.isArray(options) && options.length > 0) {
        if (key === 'merchant_type') {
          return options.map(opt => ({ text: opt, value: opt.toLowerCase() }))
        }
        return options.map(opt => ({ text: opt, value: opt }))
      }
      return this.merchantTypeOptions
    },

    getFieldModelKey(key) {
      const keyMap = {
        business_name: 'name',
        address_line_1: 'addressLine1',
        address_line_2: 'addressLine2',
        address_line_3: 'addressLine3',
        postal_code: 'postalCode',
        country_id: 'country',
        state_id: 'state',
        city_id: 'city',
        contact_no: 'contactNo',
        email: 'email',
        description: 'description',
        username: 'username',
        password: 'password',
        first_name: 'firstName',
        last_name: 'lastName',
        user_email: 'userEmail',
        user_contact_no: 'userContactNo',
        account_name: 'accountName',
        account_no: 'accountNo',
        bank_name: 'bankName',
        branch_name: 'branchName',
        branch_code: 'branchCode',
        merchant_verify_doc: 'merchantVerifyDoc',
        user_verify_doc: 'userVerifyDoc',
        bank_details_verify_doc: 'bankDetailsVerifyDoc',
      }
      return keyMap[key] || null
    },

    getFieldValue(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        return this.form[standardKey]
      }
      return this.form.custom_fields[key] || ''
    },

    setFieldValue(key, value) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = value
      } else {
        this.$set(this.form.custom_fields, key, value)
      }
    },

    getFileFieldValue(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        return this.form[standardKey]
      }
      return this.form.custom_fields[key] || null
    },

    handleFileChange(key, event) {
      const { target } = event
      const { files } = target
      const file = files[0]
      if (!file) return

      const maxSizeInMB = 2
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024
      if (file.size > maxSizeInBytes) {
        this.showErrorMessage(`File size exceeds ${maxSizeInMB} MB. Please upload a smaller file.`)
        target.value = ''
        return
      }

      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = file
      } else {
        this.$set(this.form.custom_fields, key, file)
      }
    },

    clearFileField(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = null
      } else {
        this.$set(this.form.custom_fields, key, null)
      }
    },

    // ================= LOCATION APIS =================
    async fetchCountryList() {
      try {
        const { data } = (await ResourceRepository.getCountryList()).data
        this.countryOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async fetchStateList(selectedCountry = null) {
      if (selectedCountry !== null) {
        this.form.country = selectedCountry
      }
      this.form.state = null
      this.form.city = null
      this.stateOptions = []
      this.cityOptions = []

      const countryId = selectedCountry || this.form.country
      if (!countryId) return

      try {
        const { data } = (await ResourceRepository.getStateDropdownList(countryId)).data
        this.stateOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async fetchCityList(selectedState = null) {
      if (selectedState !== null) {
        this.form.state = selectedState
      }
      this.form.city = null
      this.cityOptions = []

      const stateId = selectedState || this.form.state
      if (!stateId) return

      try {
        const { data } = (await ResourceRepository.getCityListDropdownByState(stateId)).data
        this.cityOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async getMerchantSetting() {
      try {
        const { data } = (await MerchantSettingsRepository.getMerchantSettingsResource()).data
        this.merchantType = Boolean(data.merchant_type_select)
        this.merchantVerifyDocEnable = Boolean(data.verify_document_upload_enable)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async onClickCreateMerchant() {
      this.modalLoading = true
      try {
        let verifyDocResponse = null

        const hasDefaultFiles = this.merchantVerifyDocEnable && (
          this.form.merchantVerifyDoc || this.form.bankDetailsVerifyDoc || this.form.userVerifyDoc
        )
        const hasCustomFiles = this.configuredFields.some(
          f => f.type === 'file' && f.group === 'custom' && this.form.custom_fields[f.key] instanceof File,
        )

        if (hasDefaultFiles || hasCustomFiles) {
          verifyDocResponse = await this.uploadVerificationDocuments()
          if (verifyDocResponse.status !== 200) {
            this.showErrorMessage('Verification document upload failed')
            this.modalLoading = false
            return
          }
        }

        const uploadedDocs = verifyDocResponse?.data?.data || {}

        // Clean custom fields and map uploaded URLs
        const cleanedCustomFields = {}
        Object.keys(this.form.custom_fields || {}).forEach(k => {
          const fieldConfig = this.configuredFields.find(f => f.key === k)
          if (fieldConfig && fieldConfig.type === 'file') {
            cleanedCustomFields[k] = uploadedDocs[k] || null
          } else if (this.form.custom_fields[k] === '') {
            cleanedCustomFields[k] = null
          } else {
            cleanedCustomFields[k] = this.form.custom_fields[k]
          }
        })

        const payload = {
          name: this.form.name,
          business_name: this.form.name,
          description: this.form.description,
          is_active: this.form.status === 'active' ? 'active' : 'inactive',
          merchant_verify_doc: uploadedDocs.merchant_verify_doc || null,
          bank_details: {
            account_name: this.form.accountName,
            account_number: this.form.accountNo,
            bank_name: this.form.bankName,
            branch_name: this.form.branchName,
            branch_code: this.form.branchCode,
            bank_details_verify_doc: uploadedDocs.bank_details_verify_doc || null,
          },
          user_details: {
            first_name: this.form.firstName,
            last_name: this.form.lastName,
            email: this.form.userEmail,
            user_name: this.form.username,
            password: this.form.password,
            phone: this.form.userContactNo,
            user_verify_doc: uploadedDocs.user_verify_doc || null,
          },
          address_details: {
            address_line_1: this.form.addressLine1,
            address_line_2: this.form.addressLine2,
            address_line_3: this.form.addressLine3,
            city_id: this.form.city,
            state_id: this.form.state || '',
            phone: this.form.contactNo,
            country_id: this.form.country,
            postal_code: this.form.postalCode,
            type: 'general',
            is_active: this.form.status === 'active',
            is_default: true,
          },
          contact_details: {
            phone: this.form.contactNo,
            email: this.form.email,
            is_default: true,
          },
          business_setting: {
            merchant_type: this.merchantType ? this.merchantTypeSelected : 'credit',
          },
          custom_fields: cleanedCustomFields,
        }

        await MerchantRepository.createMerchant(payload)
        this.showSuccessMessage('Merchant created successfully')
        this.open = false
        if (this.$parent && this.$parent.onClickRefresh) {
          this.$parent.onClickRefresh()
        }
        localStorage.removeItem('merchant_list')
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.modalLoading = false
      }
    },

    async uploadVerificationDocuments() {
      const formData = new FormData()
      formData.append('merchant_verify_doc', this.form.merchantVerifyDoc || '')
      formData.append('bank_details_verify_doc', this.form.bankDetailsVerifyDoc || '')
      formData.append('user_verify_doc', this.form.userVerifyDoc || '')

      this.configuredFields.forEach(field => {
        if (field.type === 'file' && field.group === 'custom') {
          const fileVal = this.form.custom_fields[field.key]
          if (fileVal instanceof File) {
            formData.append(field.key, fileVal)
          }
        }
      })

      return MerchantRepository.uploadVerificationDocuments(formData)
    },

    clearForm() {
      Object.assign(this.$data, initialState())
      this.open = true
      if (this.$refs.createMerchantForm) {
        this.$refs.createMerchantForm.reset()
      }
    },
  },
}
</script>

<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
</style>
<style lang="scss" scoped>
@import '@core/scss/vue/required.scss';
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.sub-heading {
  text-align: center;
}
.upload-cancel {
  position: absolute;
  right: 81px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
}
.position-relative {
  position: relative;
}
</style>
